/* global Blob, URL, document, setTimeout */

// Client-side ledger export helpers used by the Tenant Portal Ledger
// Download dropdown (LedgerDownloadDropdown / LedgerTabnav).
//
// Two output formats are supported:
//   - CSV : pure JS, BOM-prefixed UTF-8 so Excel opens it correctly.
//           Driven from the on-screen column schema so every tab tab
//           (Full / Payments / Charges / Invoices) exports the exact
//           rows the user is looking at.
//   - PDF : rendered with jspdf + jspdf-autotable into a Blob and
//           downloaded through the same Save-As anchor as CSV, so the
//           UX is one click — no print dialog interception. The PDF is a
//           statement layout (title + property block + subtitle) whose
//           table uses the exact same column schema as the CSV
//           (getColumnsForScope), so both documents always expose an
//           identical column set. jspdf + jspdf-autotable are heavy, so
//           they are loaded on demand via dynamic import() inside
//           buildLedgerPdf — they never enter the ledger bundle for
//           tenants who only view (never export).
//
// Eslint flat-config note: the project's globals list intentionally
// omits browser runtime globals (see eslint.config.js comment), so this
// module declares the ones it uses up top instead of leaking
// no-undef errors into pre-commit / CI.

import { getColumnsForScope } from 'portal-modules/ledger/columns/ledger-columns'
import { LEDGER_SCOPES } from 'portal-modules/ledger/utils/ledger-scope'

const CSV_MIME = 'text/csv;charset=utf-8'
const FILENAME_BASE = 'tenant-ledger'

// Filename slug per scope. Distinct from the scope identifier so the
// "Full Ledger" tab produces the more descriptive `full-ledger` segment
// (e.g. `tenant-ledger-acme-full-ledger-2026-06-19-0350.pdf`).
const SCOPE_FILENAME_SLUG = Object.freeze({
  [LEDGER_SCOPES.FULL]: 'full-ledger',
  [LEDGER_SCOPES.PAYMENTS]: 'payments',
  [LEDGER_SCOPES.CHARGES]: 'charges',
  [LEDGER_SCOPES.INVOICES]: 'invoices',
})

// Human-readable scope label rendered in the PDF subtitle. The values are
// translation keys (resolved through t() at point of use in
// buildPdfSubtitle) so the subtitle is localized to match the on-screen
// scope tabs. 'Payments' / 'Charges' / 'Invoices' deliberately reuse the
// exact keys from the LedgerTabnav tabs so a single translation serves
// both; 'Full ledger' keeps its descriptive lowercase form for the
// statement subtitle.
const SCOPE_LABEL = Object.freeze({
  [LEDGER_SCOPES.FULL]: 'Full ledger',
  [LEDGER_SCOPES.PAYMENTS]: 'Payments',
  [LEDGER_SCOPES.CHARGES]: 'Charges',
  [LEDGER_SCOPES.INVOICES]: 'Invoices',
})

export const LEDGER_EXPORT_FORMATS = Object.freeze({ PDF: 'pdf', CSV: 'csv' })

const pad2 = n => String(n).padStart(2, '0')

const buildHyphenatedTimestamp = (now = new Date()) =>
  `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
  `-${pad2(now.getHours())}${pad2(now.getMinutes())}`

// Slugifies an arbitrary string for safe use inside a filename:
// lowercased, ASCII-only, hyphen-separated. Returns '' for falsy /
// unusable input so the caller can decide whether to omit the segment.
export const slugify = value => {
  if (value === null || value === undefined) return ''
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

// Builds a filename of the form
//   tenant-ledger[-{propertySlug}][-{scopeSlug}]-{YYYY-MM-DD}-{HHMM}.{ext}
// Each optional segment is dropped (with its leading "-") when empty,
// so SSR / unconfigured tenants still get a clean filename.
//
// Backwards-compatible signature:
//   buildLedgerExportFilename('csv')                   -> base + stamp
//   buildLedgerExportFilename('csv', { scope, ... })   -> full form
//   buildLedgerExportFilename('csv', dateInstance)     -> legacy 2-arg
export const buildLedgerExportFilename = (format, optionsOrNow = {}) => {
  const ext = format === LEDGER_EXPORT_FORMATS.CSV ? 'csv' : 'pdf'
  const opts =
    optionsOrNow instanceof Date ? { now: optionsOrNow } : optionsOrNow || {}
  const { scope, propertySlug, propertyName, now = new Date() } = opts
  const stamp = buildHyphenatedTimestamp(now)
  const propSeg = slugify(propertySlug) || slugify(propertyName) || ''
  const scopeSeg = SCOPE_FILENAME_SLUG[scope] || ''
  const segments = [FILENAME_BASE]
  if (propSeg) segments.push(propSeg)
  if (scopeSeg) segments.push(scopeSeg)
  segments.push(stamp)
  return `${segments.join('-')}.${ext}`
}

const csvEscape = field => {
  const s = field === null || field === undefined ? '' : String(field)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

// Normalises the export payload into a `[{ title?, rows }]` section list.
// Callers may pass either the new section shape (Payments splits into
// Pending / Complete) or a plain row array (legacy / Full / Charges /
// Invoices single-section case), so both keep working.
const normalizeSections = input => {
  if (!Array.isArray(input) || input.length === 0) return [{ rows: [] }]
  const looksLikeSections = input.every(
    el => el && typeof el === 'object' && Array.isArray(el.rows),
  )
  return looksLikeSections ? input : [{ rows: input }]
}

// Schema-driven CSV. Headers and cell values come from the scope's column
// schema (getColumnsForScope), with the Full Ledger gaining its Sales Tax
// column only when `showSalesTax` is set. Grouped scopes (Payments) emit a
// section-title row before each titled group so the file mirrors the
// on-screen section headers.
export const buildLedgerCsv = (
  sectionsOrRows = [],
  scope = LEDGER_SCOPES.FULL,
  { showSalesTax = false } = {},
) => {
  const sections = normalizeSections(sectionsOrRows)
  const columns = getColumnsForScope(scope, { showSalesTax })
  const lines = [
    columns
      .map(column => column.header())
      .map(csvEscape)
      .join(','),
  ]
  sections.forEach((section, index) => {
    if (section.title) {
      if (index > 0) lines.push('')
      lines.push(csvEscape(section.title))
    }
    section.rows.forEach(row => {
      lines.push(
        columns.map(column => csvEscape(column.formatForExport(row))).join(','),
      )
    })
  })
  // \uFEFF (UTF-8 BOM) makes Excel treat the file as UTF-8.
  // \r\n line endings are the spreadsheet convention.
  const text = `\uFEFF${lines.join('\r\n')}\r\n`
  return new Blob([text], { type: CSV_MIME })
}

// Triggers a Save-As / direct download via a temporary anchor. Mirrors
// the pattern used in src/modules/tenant-improvements/utils/
// file-download.js but accepts a raw Blob (the helper there is shaped
// for axios responses with headers).
export const triggerBlobDownload = (blob, filename) => {
  if (
    !blob ||
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return false
  }
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  try {
    anchor.click()
  } finally {
    document.body.removeChild(anchor)
  }
  // Browsers vary on how long they need the object URL alive; 1s mirrors
  // the existing tenant-improvements file-download helper.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}

const t = (key, params) => {
  const i18n = typeof __ === 'function' ? __ : null
  if (!i18n) return key
  return params === undefined ? i18n(key) : i18n(key, params)
}

// ── PDF layout ──────────────────────────────────────────────────────
//
// The PDF table mirrors the CSV / on-screen table exactly: the same
// column set (headers + cell values) comes from getColumnsForScope(scope)
// — the single source of truth in ledger-columns.js — so a Full/Payments/
// Charges statement renders Date, Type, Source, Charges / Payments and
// Balance, and an Invoices statement renders the 7 invoice columns. Both
// documents therefore always expose an identical column set (product
// requirement, DEV-293823). Right-aligned schema columns map to
// autotable's halign: 'right'.

const PDF_PAGE_MARGIN = 40
const PDF_TITLE_FONT_SIZE = 18
const PDF_BODY_FONT_SIZE = 11
const PDF_SUBTITLE_FONT_SIZE = 10
const PDF_TABLE_FONT_SIZE = 10

const PDF_COLOR_PRIMARY = [31, 41, 55]
const PDF_COLOR_MUTED = [107, 114, 128]
const PDF_HEADER_FILL = [243, 244, 246]

const formatGeneratedTimestamp = generatedAt => {
  if (!(generatedAt instanceof Date)) return ''
  if (Number.isNaN(generatedAt.getTime())) return ''
  // Browser locale picks the date order (DD/MM vs MM/DD) per the user's
  // platform settings; the screenshot in the TDD is en-GB so it reads
  // "19/06/2026, 18:44:58" but en-US tenants get "6/19/2026, 6:44:58 PM".
  return generatedAt.toLocaleString()
}

// Bold statement title. Returns the next baseline y.
const drawPdfTitle = (doc, y) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_TITLE_FONT_SIZE)
  doc.setTextColor(...PDF_COLOR_PRIMARY)
  doc.text(t('Tenant Ledger Statement'), PDF_PAGE_MARGIN, y)
  return y + 22
}

// Keep only the property-block lines that have real content.
const pickPropertyLines = (propertyName, propertyBuilding, propertySuite) =>
  [propertyName, propertyBuilding, propertySuite].filter(
    line => typeof line === 'string' && line.trim().length > 0,
  )

// Property block (name / building / suite). Returns the next baseline y,
// adding a little extra gap only when at least one line was drawn.
const drawPdfPropertyLines = (doc, lines, startY) => {
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(PDF_BODY_FONT_SIZE)
  doc.setTextColor(...PDF_COLOR_PRIMARY)
  let y = startY
  lines.forEach(line => {
    doc.text(line, PDF_PAGE_MARGIN, y)
    y += 14
  })
  return lines.length > 0 ? y + 4 : y
}

// "<Scope> • Generated <timestamp>" subtitle string (timestamp optional).
// The scope label is run through t() so it is localized like the on-screen
// tabs rather than printed in English regardless of the active locale.
const buildPdfSubtitle = (scope, generatedAt) => {
  const scopeLabel = t(SCOPE_LABEL[scope] || SCOPE_LABEL[LEDGER_SCOPES.FULL])
  const timestamp = formatGeneratedTimestamp(generatedAt)
  return timestamp
    ? `${scopeLabel} \u2022 ${t('Generated {%s, 0}', [timestamp])}`
    : scopeLabel
}

// Muted subtitle ("<Scope> • Generated <timestamp>"). Returns the next
// baseline y. Uses the muted grey (not an accent colour) so it reads as
// secondary metadata beneath the property block.
const drawPdfSubtitle = (doc, subtitle, y) => {
  doc.setFontSize(PDF_SUBTITLE_FONT_SIZE)
  doc.setTextColor(...PDF_COLOR_MUTED)
  doc.text(subtitle, PDF_PAGE_MARGIN, y)
  return y + 14
}

const drawPdfHeader = ({
  doc,
  scope,
  propertyName,
  propertyBuilding,
  propertySuite,
  generatedAt,
}) => {
  let y = drawPdfTitle(doc, PDF_PAGE_MARGIN + 16)
  const propertyLines = pickPropertyLines(
    propertyName,
    propertyBuilding,
    propertySuite,
  )
  y = drawPdfPropertyLines(doc, propertyLines, y)
  return drawPdfSubtitle(doc, buildPdfSubtitle(scope, generatedAt), y)
}

// Section heading (e.g. "Pending Payments") drawn above a grouped table.
// Returns the next baseline y.
const drawPdfSectionTitle = (doc, title, y) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_BODY_FONT_SIZE)
  doc.setTextColor(...PDF_COLOR_PRIMARY)
  doc.text(title, PDF_PAGE_MARGIN, y)
  doc.setFont('helvetica', 'normal')
  return y + 8
}

// Table body built from the shared column schema so every PDF cell is the
// exact value the CSV writes (same getColumnsForScope source). An empty
// ledger renders a single centered empty-state row spanning all columns.
const buildLedgerTableBody = (rows, columns) =>
  rows.length
    ? rows.map(row => columns.map(column => column.formatForExport(row)))
    : [
        [
          {
            content: t('No transactions to export'),
            colSpan: columns.length,
            styles: {
              halign: 'center',
              textColor: PDF_COLOR_MUTED,
              fontStyle: 'italic',
            },
          },
        ],
      ]

// jspdf-autotable options for the ledger statement table. Columns, headers
// and cell values all come from the shared schema (getColumnsForScope), so
// the PDF and CSV always expose an identical column set. Right-aligned
// schema columns map to autotable's halign: 'right'.
const buildLedgerTableConfig = ({ columns, rows, startY }) => ({
  startY: startY + 6,
  margin: { left: PDF_PAGE_MARGIN, right: PDF_PAGE_MARGIN },
  head: [columns.map(column => column.header())],
  body: buildLedgerTableBody(rows, columns),
  styles: {
    font: 'helvetica',
    fontSize: PDF_TABLE_FONT_SIZE,
    textColor: PDF_COLOR_PRIMARY,
    cellPadding: { top: 8, right: 6, bottom: 8, left: 6 },
    lineColor: [229, 231, 235],
    lineWidth: 0.5,
    overflow: 'linebreak',
  },
  headStyles: {
    fillColor: PDF_HEADER_FILL,
    textColor: PDF_COLOR_PRIMARY,
    fontStyle: 'bold',
  },
  // Per-column halign drives BOTH the header and the body cells so each
  // column's header and values stay on the same alignment. The numeric
  // columns (schema align 'right' — Charges / Payments, Balance) are
  // centered in the statement per product; text columns stay left.
  columnStyles: columns.reduce((styles, column, index) => {
    styles[index] = { halign: column.align === 'right' ? 'center' : 'left' }
    return styles
  }, {}),
})

// Renders the ledger into a PDF Blob ready for triggerBlobDownload.
// No print dialog, no iframe — the user gets a single download just
// like the CSV path. jspdf + jspdf-autotable are imported lazily here so
// they are only fetched the first time a tenant actually exports a PDF
// (keeps them out of the ledger bundle for view-only users). Returns a
// Promise<Blob>.
export const buildLedgerPdf = async ({
  sections,
  transactions = [],
  scope = LEDGER_SCOPES.FULL,
  showSalesTax = false,
  propertyName,
  propertyBuilding,
  propertySuite,
  generatedAt = new Date(),
} = {}) => {
  const [jsPdfModule, autoTableModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ])
  const { jsPDF } = jsPdfModule
  const autoTable = autoTableModule.default || autoTableModule
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })
  let cursorY = drawPdfHeader({
    doc,
    scope,
    propertyName,
    propertyBuilding,
    propertySuite,
    generatedAt,
  })
  const columns = getColumnsForScope(scope, { showSalesTax })
  const resolvedSections = normalizeSections(
    sections !== undefined ? sections : transactions,
  )
  resolvedSections.forEach((section, index) => {
    if (section.title) {
      cursorY = drawPdfSectionTitle(
        doc,
        section.title,
        cursorY + (index > 0 ? 8 : 0),
      )
    }
    autoTable(
      doc,
      buildLedgerTableConfig({ columns, rows: section.rows, startY: cursorY }),
    )
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : cursorY
    cursorY = finalY + 16
  })
  return doc.output('blob')
}

// Convenience entry point: picks the right helper for the requested
// format. Returns a Promise resolving to a result envelope so callers
// (the toast in LedgerTabnav, telemetry, etc.) get the count / scope /
// filename they need without re-deriving them. Resolves to `null` when
// the environment doesn't support the operation (e.g. SSR), the format
// is unknown, or the on-demand PDF library fails to load. Async because
// the PDF generator lazy-loads jspdf via dynamic import().
export const exportLedger = async ({
  format,
  sections,
  transactions = [],
  scope = LEDGER_SCOPES.FULL,
  showSalesTax = false,
  propertyName,
  propertySlug,
  propertyBuilding,
  propertySuite,
} = {}) => {
  if (
    format !== LEDGER_EXPORT_FORMATS.CSV &&
    format !== LEDGER_EXPORT_FORMATS.PDF
  ) {
    return null
  }
  const filename = buildLedgerExportFilename(format, {
    scope,
    propertyName,
    propertySlug,
  })
  const payload = sections !== undefined ? sections : transactions
  let blob
  try {
    blob =
      format === LEDGER_EXPORT_FORMATS.CSV
        ? buildLedgerCsv(payload, scope, { showSalesTax })
        : await buildLedgerPdf({
            sections,
            transactions,
            scope,
            showSalesTax,
            propertyName,
            propertyBuilding,
            propertySuite,
          })
  } catch {
    // e.g. the lazy jspdf chunk failed to load — fail closed so the
    // caller can surface a non-success state instead of crashing.
    return null
  }
  if (!triggerBlobDownload(blob, filename)) return null
  return {
    ok: true,
    format,
    count: normalizeSections(payload).reduce(
      (total, section) => total + section.rows.length,
      0,
    ),
    scope: scope || null,
    filename,
  }
}
